import { z } from 'zod';

// =============================================================================
// Actividades Previas — Checklists de seguridad ST-SIMEC
// Módulo standalone (NO es el checklist de pauta de tickets/).
// Spec: SIME/docs/checklists/MAPPING.md
// =============================================================================

// ── Enums (espejo de los enums Prisma) ──────────────────────────────────────
export const CampoTipoEnum = z.enum(['TEXT', 'DATE', 'TIME']);
export type CampoTipo = z.infer<typeof CampoTipoEnum>;

export const EscalaRespuestaEnum = z.enum(['SI_NO_NA', 'BUENO_MALO_NA', 'DATOS']);
export type EscalaRespuesta = z.infer<typeof EscalaRespuestaEnum>;

export const EstadoActPreviaEnum = z.enum(['BORRADOR', 'COMPLETADO', 'FIRMADO']);
export type EstadoActPrevia = z.infer<typeof EstadoActPreviaEnum>;

export const MetodoFirmaEnum = z.enum(['IN_APP', 'EMAIL']);
export type MetodoFirma = z.infer<typeof MetodoFirmaEnum>;

export const FirmaLinkStatusEnum = z.enum(['PENDIENTE', 'FIRMADO', 'EXPIRADO']);
export type FirmaLinkStatus = z.infer<typeof FirmaLinkStatusEnum>;

/** Valores válidos de una respuesta según la escala de su sección. */
export const VALOR_POR_ESCALA: Record<EscalaRespuesta, readonly string[]> = {
  SI_NO_NA: ['SI', 'NO', 'NA'],
  BUENO_MALO_NA: ['BUENO', 'MALO', 'NA'],
  DATOS: [], // texto libre
} as const;

// ── Plantilla (template): campos + secciones + ítems ────────────────────────
export const PlantillaCampoInputSchema = z.object({
  key: z.string().min(1).max(60),
  label: z.string().min(1).max(120),
  tipo: CampoTipoEnum.default('TEXT'),
  orden: z.number().int().min(0).default(0),
  requerido: z.boolean().default(false),
  cellRef: z.string().max(10).nullable().optional(),
});
export type PlantillaCampoInput = z.infer<typeof PlantillaCampoInputSchema>;

export const PlantillaItemInputSchema = z.object({
  texto: z.string().min(1),
  orden: z.number().int().min(0).default(0),
  requerido: z.boolean().default(true),
  cellRefRespuesta: z.string().max(10).nullable().optional(),
});
export type PlantillaItemInput = z.infer<typeof PlantillaItemInputSchema>;

export const PlantillaSeccionInputSchema = z.object({
  titulo: z.string().min(1),
  escala: EscalaRespuestaEnum,
  orden: z.number().int().min(0).default(0),
  items: z.array(PlantillaItemInputSchema).default([]),
});
export type PlantillaSeccionInput = z.infer<typeof PlantillaSeccionInputSchema>;

export const CreatePlantillaSchema = z.object({
  codigo: z.string().min(1).max(40),
  nombre: z.string().min(1).max(200),
  version: z.string().min(1).max(10),
  fechaDoc: z.string().datetime().nullable().optional(),
  titulo: z.string().min(1),
  scopeText: z.string().nullable().optional(),
  footerNote: z.string().nullable().optional(),
  conclusionLabel: z.string().nullable().optional(),
  inspectorRolLabel: z.string().nullable().optional(),
  templateXlsxKey: z.string().min(1),
  campos: z.array(PlantillaCampoInputSchema).default([]),
  secciones: z.array(PlantillaSeccionInputSchema).default([]),
});
export type CreatePlantillaInput = z.infer<typeof CreatePlantillaSchema>;

export const UpdatePlantillaSchema = CreatePlantillaSchema.partial().extend({
  activo: z.boolean().optional(),
});
export type UpdatePlantillaInput = z.infer<typeof UpdatePlantillaSchema>;

export const PlantillaListFilterSchema = z.object({
  activo: z.coerce.boolean().optional(),
});
export type PlantillaListFilter = z.infer<typeof PlantillaListFilterSchema>;

// ── Instancia (formulario llenado) ──────────────────────────────────────────
export const CreateActividadPreviaSchema = z.object({
  plantillaId: z.number().int().positive(),
  ambitoId: z.number().int().positive(),
  otId: z.number().int().positive().nullable().optional(),
  headerValues: z.record(z.string(), z.string()).default({}),
});
export type CreateActividadPreviaInput = z.infer<typeof CreateActividadPreviaSchema>;

export const UpdateHeaderSchema = z.object({
  headerValues: z.record(z.string(), z.string()),
});
export type UpdateHeaderInput = z.infer<typeof UpdateHeaderSchema>;

export const UpdateRespuestaSchema = z.object({
  valor: z.string().max(500).nullable().optional(),
  observaciones: z.string().nullable().optional(),
});
export type UpdateRespuestaInput = z.infer<typeof UpdateRespuestaSchema>;

export const UpdateConclusionSchema = z.object({
  conclusionApto: z.boolean().nullable().optional(),
  generalDesc: z.string().nullable().optional(),
});
export type UpdateConclusionInput = z.infer<typeof UpdateConclusionSchema>;

export const ActividadPreviaListFilterSchema = z.object({
  ambitoId: z.coerce.number().int().positive().optional(),
  plantillaId: z.coerce.number().int().positive().optional(),
  estado: EstadoActPreviaEnum.optional(),
  otId: z.coerce.number().int().positive().optional(),
});
export type ActividadPreviaListFilter = z.infer<typeof ActividadPreviaListFilterSchema>;

// ── Firma in-app (líder en terreno captura la firma al momento) ─────────────
// El firmante es SIEMPRE el usuario autenticado (id+nombre del token). El cliente
// sólo envía la imagen — no se acepta inspectorUserId del body (evita spoofing).
export const FirmaInAppSchema = z.object({
  firmaBase64: z.string().min(1), // dataURL PNG
});
export type FirmaInAppInput = z.infer<typeof FirmaInAppSchema>;

// ── Firma por email (FASE 3b) ───────────────────────────────────────────────
// Consumido por el módulo actividades-previas (solicitar-firma + rutas públicas
// /firma-actividad). Espejo del patrón `charla` (usuarioId? + nombreManual?).
// Invariantes alineadas con `lote.ts`: el código de verificación es de 6 dígitos.
export const SolicitarFirmaEmailSchema = z
  .object({
    recipientUsuarioId: z.number().int().positive().optional(),
    recipientNombre: z.string().max(200).optional(),
    recipientEmail: z.string().email(),
    rol: z.string().max(120).optional(),
  })
  .refine((d) => d.recipientUsuarioId != null || (d.recipientNombre?.trim().length ?? 0) > 0, {
    message: 'Debe indicar un usuario sugerido o un nombre manual',
    path: ['recipientNombre'],
  });
export type SolicitarFirmaEmailInput = z.infer<typeof SolicitarFirmaEmailSchema>;

// Firma desde la página pública (firmante externo): código de verificación + imagen.
export const FirmaExternaSchema = z.object({
  codigo: z.string().regex(/^\d{6}$/, 'El código de verificación debe ser de 6 dígitos'),
  firmaBase64: z.string().min(1),
});
export type FirmaExternaInput = z.infer<typeof FirmaExternaSchema>;
